import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  Table,
  Input,
  Select,
  Button,
  Dropdown,
  message,
  Alert,
} from 'antd';
import type { MenuProps } from 'antd';
import {
  ShopOutlined,
  CheckCircleOutlined,
  StopOutlined,
  ClockCircleOutlined,
  SearchOutlined,
  DownOutlined,
  EllipsisOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import api from '../services/api';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { StatusBadge } from '../design-system';
import { formatVnDate } from '../utils/vnDateTime';
import { MerchantAvatar } from './MerchantAvatar';

const PAGE_SIZE = 5;
const SUMMARY_LIMIT = 10000;

export interface MerchantRecord {
  id: number;
  name: string;
  email: string;
  phone?: string;
  address?: string;
  is_active?: boolean;
  created_at: string;
  restaurantId?: number;
  restaurantName?: string;
  restaurantAddress?: string;
  restaurant_image?: string;
}

interface MerchantsResponse {
  success: boolean;
  data: MerchantRecord[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
}

function getInitials(name: string | undefined | null): string {
  if (!name || !name.trim()) return '?';
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }
  return name.slice(0, 2).toUpperCase();
}

const AVATAR_COLORS = [
  '#2563eb',
  '#7c3aed',
  '#ea580c',
  '#db2777',
  '#4b5563',
  '#047857',
];

function avatarColor(name: string | undefined | null): string {
  if (!name) return AVATAR_COLORS[0];
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h << 5) - h + name.charCodeAt(i);
  return AVATAR_COLORS[Math.abs(h) % AVATAR_COLORS.length];
}

/** Single source of truth: is_active (true=active, false=suspended, null/undefined=pending) */
function deriveStatus(
  record: MerchantRecord
): 'active' | 'suspended' | 'pending' {
  const isActive = record.is_active;
  if (isActive === true) return 'active';
  if (isActive === false) return 'suspended';
  return 'pending';
}

function statusLabel(status: string): string {
  const s = (status || '').toLowerCase();
  const map: Record<string, string> = {
    active: 'Active',
    suspended: 'Suspended',
    pending: 'Pending',
  };
  return map[s] || status || '–';
}

export default function MerchantList() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const forceRefresh = searchParams.get('refresh') === '1';
  const [list, setList] = useState<MerchantRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string | undefined>(
    undefined
  );
  const [categoryFilter, setCategoryFilter] = useState<string | undefined>(
    undefined
  );
  const [summaryCounts, setSummaryCounts] = useState({
    total: 0,
    active: 0,
    pending: 0,
    suspended: 0,
  });

  const fetchList = useCallback(async () => {
    try {
      setLoading(true);
      const params: Record<string, string | number> = {
        page,
        limit: PAGE_SIZE,
      };
      if (statusFilter) params.status = statusFilter;
      if (search.trim()) params.search = search.trim();
      if (forceRefresh) params.refresh = '1';
      const res = await api.get<MerchantsResponse>('/merchants', { params });
      const body = res.data;
      if (body.success && Array.isArray(body.data)) {
        setList(body.data);
        const pag = body.pagination;
        if (pag && Number.isFinite(pag.total)) {
          setTotal(pag.total);
        }
        if (forceRefresh) {
          searchParams.delete('refresh');
          setSearchParams(searchParams, { replace: true });
        }
      }
      setError(null);
    } catch (e: unknown) {
      const ax = e as {
        response?: { data?: { message?: string }; status?: number };
        message?: string;
      };
      setError(
        ax.response?.data?.message || ax.message || 'Failed to load merchants'
      );
      message.error('Failed to load merchants');
    } finally {
      setLoading(false);
    }
  }, [page, statusFilter, search, forceRefresh, searchParams, setSearchParams]);

  const fetchSummary = useCallback(async () => {
    try {
      const res = await api.get<MerchantsResponse>('/merchants', {
        params: { page: 1, limit: SUMMARY_LIMIT },
      });
      const body = res.data;
      if (!body.success || !Array.isArray(body.data)) return;
      const data = body.data;
      const pag = body.pagination;
      const totalCount =
        pag && Number.isFinite(pag.total) ? pag.total : data.length;
      const active = data.filter(m => deriveStatus(m) === 'active').length;
      const pending = data.filter(m => deriveStatus(m) === 'pending').length;
      const suspended = data.filter(
        m => deriveStatus(m) === 'suspended'
      ).length;
      setSummaryCounts({
        total: totalCount,
        active,
        pending,
        suspended,
      });
    } catch {
      // keep previous summary
    }
  }, []);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);

  const start = total === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const end = Math.min(page * PAGE_SIZE, total);

  const columns = [
    {
      title: 'MERCHANT',
      key: 'merchant',
      render: (_: unknown, record: MerchantRecord) => (
        <div className="admin-merchants-merchant">
          <MerchantAvatar
            name={record.restaurantName || record.name}
            imageUrl={record.restaurant_image}
            size={40}
            borderRadius={10}
            className="admin-merchants-logo"
          />
          <div className="admin-merchants-merchant-text">
            <div className="admin-merchants-merchant-name">
              {record.restaurantName || record.name || '–'}
            </div>
            <div className="admin-merchants-merchant-email">
              {record.email || '–'}
            </div>
          </div>
        </div>
      ),
    },
    // {
    //   title: 'CATEGORY',
    //   key: 'category',
    //   align: 'center' as const,
    //   render: () => <span className="admin-merchants-category-pill">–</span>,
    // },
    {
      title: 'LOCATION',
      key: 'location',
      align: 'center' as const,
      render: (_: unknown, record: MerchantRecord) =>
        record.restaurantAddress || '–',
    },
    {
      title: 'JOIN DATE',
      dataIndex: 'created_at',
      key: 'join_date',
      align: 'center' as const,
      render: (v: string) => formatVnDate(v, 'ddd, MMM D, YYYY'),
    },
    {
      title: 'STATUS',
      key: 'status',
      align: 'center' as const,
      render: (_: unknown, record: MerchantRecord) => {
        const status = deriveStatus(record);
        return (
          <StatusBadge
            status={status}
            type="user"
            customText={statusLabel(status)}
          />
        );
      },
    },
    {
      title: 'ACTIONS',
      key: 'actions',
      width: 64,
      align: 'right' as const,
      render: (_: unknown, record: MerchantRecord) => (
        <Dropdown
          menu={{
            items: [
              {
                key: 'view',
                label: 'View',
                onClick: () => navigate(`/merchants/${record.id}`),
              },
            ] as MenuProps['items'],
          }}
          trigger={['click']}
          placement="bottomRight"
        >
          <button
            type="button"
            className="admin-merchants-actions-btn"
            aria-label="Actions"
            onClick={e => e.stopPropagation()}
          >
            <EllipsisOutlined />
          </button>
        </Dropdown>
      ),
    },
  ];

  return (
    <div className="admin-merchants-page">
      <header className="admin-merchants-header">
        <h1 className="admin-merchants-title">Merchant Management</h1>
        <nav className="admin-merchants-breadcrumb">
          <Link to="/">Dashboard</Link>
          <span className="admin-merchants-breadcrumb-sep">&gt;</span>
          <span className="admin-merchants-breadcrumb-current">Merchants</span>
        </nav>
      </header>

      {error && (
        <Alert
          type="error"
          message={error}
          closable
          onClose={() => setError(null)}
          className="admin-merchants-alert"
        />
      )}

      <section className="admin-merchants-cards">
        <Card className="admin-merchants-card">
          <div className="admin-merchants-card-body">
            <div className="admin-merchants-card-content">
              <div className="admin-merchants-card-title">Total Merchants</div>
              <div className="admin-merchants-card-value">
                {summaryCounts.total.toLocaleString('en-US')}
              </div>
            </div>
            <div className="admin-merchants-card-icon admin-merchants-card-icon-blue">
              <ShopOutlined />
            </div>
          </div>
        </Card>
        <Card className="admin-merchants-card">
          <div className="admin-merchants-card-body">
            <div className="admin-merchants-card-content">
              <div className="admin-merchants-card-title">Active</div>
              <div className="admin-merchants-card-value">
                {summaryCounts.active.toLocaleString('en-US')}
              </div>
            </div>
            <div className="admin-merchants-card-icon admin-merchants-card-icon-green">
              <CheckCircleOutlined />
            </div>
          </div>
        </Card>
        <Card className="admin-merchants-card">
          <div className="admin-merchants-card-body">
            <div className="admin-merchants-card-content">
              <div className="admin-merchants-card-title">Pending Approval</div>
              <div className="admin-merchants-card-value">
                {summaryCounts.pending.toLocaleString('en-US')}
              </div>
            </div>
            <div className="admin-merchants-card-icon admin-merchants-card-icon-orange">
              <ClockCircleOutlined />
            </div>
          </div>
        </Card>
        <Card className="admin-merchants-card">
          <div className="admin-merchants-card-body">
            <div className="admin-merchants-card-content">
              <div className="admin-merchants-card-title">Suspended</div>
              <div className="admin-merchants-card-value">
                {summaryCounts.suspended.toLocaleString('en-US')}
              </div>
            </div>
            <div className="admin-merchants-card-icon admin-merchants-card-icon-red">
              <StopOutlined />
            </div>
          </div>
        </Card>
      </section>

      <section className="admin-merchants-filters">
        <div className="admin-merchants-filters-left">
          <Input
            placeholder="Search merchant name or email..."
            prefix={<SearchOutlined className="admin-merchants-input-prefix" />}
            value={search}
            onChange={e => setSearch(e.target.value)}
            onPressEnter={() => {
              setPage(1);
              fetchList();
            }}
            className="admin-merchants-search-input"
            allowClear
          />
        </div>
        <div className="admin-merchants-filters-middle">
          {/* <Select
            placeholder="All Categories"
            suffixIcon={<DownOutlined />}
            className="admin-merchants-select"
            value={categoryFilter ?? 'all'}
            onChange={v => setCategoryFilter(v === 'all' ? undefined : v)}
            options={[{ value: 'all', label: 'All Categories' }]}
          /> */}
          <Select
            placeholder="All Status"
            suffixIcon={<DownOutlined />}
            className="admin-merchants-select"
            value={statusFilter ?? 'all'}
            onChange={v => {
              setStatusFilter(v === 'all' ? undefined : v);
              setPage(1);
            }}
            options={[
              { value: 'all', label: 'All Status' },
              { value: 'active', label: 'Active' },
              { value: 'pending', label: 'Pending' },
              { value: 'suspended', label: 'Suspended' },
            ]}
          />
        </div>
        <div className="admin-merchants-filters-right">
          <Button
            type="primary"
            icon={<PlusOutlined />}
            className="admin-merchants-add-btn"
            onClick={() => navigate('/merchants/create')}
          >
            Add Merchant
          </Button>
        </div>
      </section>

      <Card className="admin-merchants-table-card">
        <Table
          columns={columns}
          dataSource={list}
          rowKey="id"
          loading={loading}
          pagination={false}
          className="admin-merchants-table"
          onRow={record => ({
            onClick: () => navigate(`/merchants/${record.id}`),
            style: { cursor: 'pointer' },
          })}
        />
      </Card>

      <footer className="admin-merchants-pagination">
        <span className="admin-merchants-pagination-text">
          Showing {start} to {end} of {total.toLocaleString('en-US')} results
        </span>
        <div className="admin-merchants-pagination-btns">
          <Button
            disabled={page <= 1}
            onClick={() => setPage(p => Math.max(1, p - 1))}
          >
            Previous
          </Button>
          <Button
            disabled={page >= Math.ceil(total / PAGE_SIZE) || total === 0}
            onClick={() => setPage(p => p + 1)}
          >
            Next
          </Button>
        </div>
      </footer>
    </div>
  );
}
